import type { Option } from "../../core/option.ts";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";

import type { CellEntry } from "../../core/dtype.ts";

/**
 * Lane views over one `SourceRow.peekCells` read — the SDK-internal
 * rows counterpart of `@tento/lona`'s `TaggedCells`.
 *
 * `SourceRow` has one read door (`peekCells(range)` / `peekCells(null)`), so a reader
 * never picks a store and cannot miss a cell that routed to the other lane.
 * A reader that genuinely consumes one lane (a text renderer reads the date
 * lane; a sticker overlay reads the timepoint lane) reads through that door
 * and then takes its lane here. The `kind` tag is dropped, so a returned entry
 * is the stored shape (`CellEntry` / `CellEntry.Timepoint`) — spreading one
 * into a derived cell never leaks `kind` into a store.
 *
 * Not on a barrel: SDK code imports it by module path.
 */
export namespace SourceCells {
  /**
   * One entry of a source row's read, tagged by its lane: the shape of
   * `SourceRow.Cell`, spelled here so the lane views do not import the row
   * class (a `SourceRow.Cell` is one).
   */
  export type Cell =
    | (CellEntry & { readonly kind: "date" })
    | (CellEntry.Timepoint & { readonly kind: "timepoint" });

  /** A row with the one read door (`SourceRow` is one). */
  export interface Readable<C extends Cell> {
    /** The row's cells over `range` (`null`: every cell). */
    peekCells(range: Option<NaiveDate.Range>): C[];
  }

  /** `row.peekCells(range)`, or `row.peekCells(null)` when `range` is `null`. */
  export function read<C extends Cell>(row: Readable<C>, range: Option<NaiveDate.Range>): C[] {
    return row.peekCells(range);
  }

  /** The date-keyed (`kind: "date"`) entries, in read order. */
  export function dates(cells: readonly Cell[]): CellEntry[] {
    const out: CellEntry[] = [];
    for (const cell of cells) {
      if (cell.kind !== "date") continue;
      const { kind: _kind, ...entry } = cell;
      out.push(entry);
    }
    return out;
  }

  /** The timepoint (`kind: "timepoint"`) entries, in read order (sorted by start). */
  export function customs(cells: readonly Cell[]): CellEntry.Timepoint[] {
    const out: CellEntry.Timepoint[] = [];
    for (const cell of cells) {
      if (cell.kind !== "timepoint") continue;
      const { kind: _kind, ...entry } = cell;
      out.push(entry);
    }
    return out;
  }

  /**
   * The timepoint entries whose start falls in `range` (start inclusive, end
   * exclusive — the store's own custom-range filter). For a window that is not
   * a whole-day range (a rendered column span), which `peekCells`'s UTC day
   * bounds cannot express; pass `row.peekCells(null)`.
   */
  export function customsStartingIn(
    cells: readonly Cell[],
    range: DateTime.Range<Utc>,
  ): CellEntry.Timepoint[] {
    return customs(cells).filter((entry) =>
      entry.dtr.start.mse >= range.start.mse && entry.dtr.start.mse < range.end.mse
    );
  }
}
